import type { Address } from 'viem';

export const PRESALE_ADDRESS = (import.meta.env.VITE_AIML_PRESALE_ADDRESS ||
  '0x8E8dE6045891E729aDAd6f8fFEA1270734943bCf') as Address;

export const AIML_TOKEN_ADDRESS = (import.meta.env.VITE_AIML_TOKEN_ADDRESS ||
  '0xd8cc37e68bdE249f5D37b2017341D6656b10103c') as Address;

export const USDT_ADDRESS = (import.meta.env.VITE_USDT_ADDRESS ||
  '0xc2132D05D31c914a87C6611C10748AEb04B58e8F') as Address;

export const USDT_DECIMALS = 6;
export const AIML_DECIMALS = 18;

/**
 * AIMLPreSale contract ABI — verified against the deployed contract on Polygon
 * at 0x8E8dE6045891E729aDAd6f8fFEA1270734943bCf.
 *
 * Notable functions:
 *   - buyTokensWithUSDT(usdtAmount)         — the buy entry point
 *   - getTokenAmountUSDT(amountUSDT) view   — quote: returns AIML for a USDT input
 *   - usdRaised() view                      — total USDT raised so far
 *   - usdRate() view                        — current rate setting
 *   - paused() view                         — whether the sale is paused
 *   - getPriceInUSD() view                  — AIML price in USD (1 AIML = ? USD)
 */
export const PRESALE_ABI = [
  // -- WRITES --
  {
    type: 'function',
    name: 'buyTokensWithUSDT',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'usdtAmount', type: 'uint256' }],
    outputs: [],
  },

  // -- READS --
  {
    type: 'function',
    name: 'getTokenAmountUSDT',
    stateMutability: 'view',
    inputs: [{ name: 'amountUSDT', type: 'uint256' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'getPriceInUSD',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'usdRaised',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'usdRate',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'paused',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    type: 'function',
    name: 'owner',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
  },
  {
    type: 'function',
    name: 'token',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
  },
  {
    type: 'function',
    name: 'usdt',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'address' }],
  },

  // -- EVENTS --
  {
    type: 'event',
    name: 'TokensPurchased',
    inputs: [
      { name: 'buyer', type: 'address', indexed: true },
      { name: 'usdtAmount', type: 'uint256', indexed: false },
      { name: 'amount', type: 'uint256', indexed: false },
    ],
  },
] as const;

/** ERC-20 ABI subset for USDT balance + allowance + approve. */
export const ERC20_ABI = [
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ name: '', type: 'bool' }],
  },
  {
    type: 'function',
    name: 'decimals',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ name: '', type: 'uint8' }],
  },
] as const;
